<script setup lang="ts">
import { computed } from 'vue'
import { NBreadcrumb, NBreadcrumbItem } from 'naive-ui'
import { RouterLink } from 'vue-router'
import type { WikiPageDetail } from '../stores/wiki_pages'
import { categoryLabel } from '../composables/categoryLabel'
import { GENERAL_BUCKET, GENERAL_BUCKET_LABEL } from '../composables/categoryBucket'
import { formatAbsoluteDate, formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { entityTypeBadge, ENTITY_TYPE_BADGE_BASE_CLASSES } from '../composables/entityTypeBadge'
import WikiPageMetaActions from './WikiPageMetaActions.vue'

// Self-contained meta bar for the wiki page viewer: breadcrumb,
// provenance/entity badges, updated-at timestamp. Admin actions
// (Share/Edit/Rename/Export/Delete) are split into WikiPageMetaActions
// (#1097) so this view stays under the 300-line hard limit.
//
// The parent is responsible for:
//   - rendering this only when a page is selected (`page` is non-null)
//   - listening to `back` (mobile-only sidebar collapse toggle)
//   - listening to `cleared` to drop its local "selected" refs after
//     a successful delete (the URL change is handled in
//     WikiPageMetaActions, but the parent's separate `selectedId` /
//     `selectedDetail` state isn't).
const props = defineProps<{
  page: WikiPageDetail
  isAdmin: boolean
  showBack: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'cleared'): void
}>()

const now = useNow()
const updatedAt = computed(() => formatDate(props.page.updated_at, now.value))
const updatedAtAbsolute = computed(() => formatAbsoluteDate(props.page.updated_at))

// Synthesis-origin pages get a provenance badge in the meta bar.
const isSynthesisOrigin = computed(() => props.page.origin === 'synthesis')

// Entities/ classification badge — mirrors the Synthesis badge's
// slot/styling but color-coded per type so a mixed set of pages (people,
// orgs, products, projects) scans quickly without reading every label.
const entityBadge = computed(() => entityTypeBadge(props.page.entity_type))

// Display title for the breadcrumb's last (current-page) crumb. Falls
// back to the path's filename when the page row has no title yet — the
// breadcrumb still reads sensibly instead of going blank during a
// brief catalog-load gap.
const pageTitle = computed(() => props.page.title || props.page.path.split('/').pop() || props.page.path)
// Breadcrumb crumb shape for the category slot. Top-level pages
// (`page.category` null/empty, e.g. wiki/index.md) get a synthetic
// "General" crumb routed to /c/general so the user always has a path
// from any page back up to its sidebar group. Named categories use the
// stored lowercase form for the URL and the capitalised label for the
// crumb text.
const categoryCrumb = computed<{ label: string; to: string }>(() => {
  if (props.page.category) {
    return { label: categoryLabel(props.page.category), to: `/c/${props.page.category}` }
  }
  return { label: GENERAL_BUCKET_LABEL, to: `/c/${GENERAL_BUCKET}` }
})
</script>

<template>
  <!-- Page meta bar: category tag · updated date on the left, admin
       actions on the right. On mobile the ← Back affordance lives here
       too (leftmost slot) so the bar doubles as the "collapse sidebar"
       control. All elements vertically centred on one line; the back
       arrow and actions meet WCAG 2.5.5 via min-h-[44px] on the bar. -->
  <div class="mb-2 border-b border-white/10 flex items-center gap-2 min-h-[44px] text-xs text-white/40">
    <button
      v-if="showBack"
      type="button"
      class="hidden max-sm:flex items-center gap-1 shrink-0 text-white/60 hover:text-white transition-colors"
      aria-label="Back to page list"
      @click="emit('back')"
    >
      <span aria-hidden="true">←</span>
      <span>Back</span>
    </button>
    <div class="flex items-center gap-3 flex-1 min-w-0">
      <!-- Wiki path as a real breadcrumb: Home › category › page title.
           Home goes to the root catalog. The category crumb navigates
           to `/c/<lowercase>` — a real landing page that lists every
           page in the category — so the click leads somewhere visibly
           different, not just to a sidebar tweak. The page-title crumb
           is non-interactive (you're already there).
           `overflow-hidden text-ellipsis whitespace-nowrap` live on the
           NBreadcrumb root itself, not the inner title span: Naive UI
           renders the root as `display: block; white-space: nowrap` with
           no clipping of its own, so a flex-shrunk root whose crumbs are
           wider than its box paints straight over the next flex sibling
           (a badge) instead of truncating — a per-item `max-w-[Nch]`
           truncate never engages because the item never actually
           shrinks. Clipping/ellipsizing the whole line at the root is
           what keeps the bar one line on any viewport. -->
      <NBreadcrumb class="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap" data-testid="page-breadcrumb">
        <NBreadcrumbItem>
          <RouterLink to="/" class="!text-white/60 hover:!text-white no-underline">Home</RouterLink>
        </NBreadcrumbItem>
        <NBreadcrumbItem>
          <RouterLink
            :to="categoryCrumb.to"
            class="!text-white/60 hover:!text-white no-underline"
            data-testid="breadcrumb-category"
            >{{ categoryCrumb.label }}</RouterLink
          >
        </NBreadcrumbItem>
        <NBreadcrumbItem>
          <span class="text-white/80">{{ pageTitle }}</span>
        </NBreadcrumbItem>
      </NBreadcrumb>
      <!-- Provenance badge: marks a page the Synthesis pipeline
           generated so a reader knows the claims were machine-inferred
           from other pages rather than sourced directly. -->
      <span
        v-if="isSynthesisOrigin"
        class="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-amber-500/15 text-amber-200 border border-amber-500/25"
        data-testid="synthesis-origin-badge"
        title="Generated by the Synthesis pipeline"
        >Synthesis</span
      >
      <!-- Entities/ type classification badge: person/org/product/
           project, color-coded via entityTypeBadge so a reader can tell
           at a glance what kind of entity this page describes. -->
      <span
        v-if="entityBadge"
        class="shrink-0"
        :class="[ENTITY_TYPE_BADGE_BASE_CLASSES, entityBadge.classes]"
        data-testid="entity-type-badge"
        >{{ entityBadge.label }}</span
      >
      <!-- The visible label is relative ("3 days ago") for at-a-glance
           freshness; the title attribute pins the absolute date so users
           can tell whether "3 months ago" was January or March without
           navigating away. Hidden on narrow viewports so the breadcrumb
           keeps the room it needs. -->
      <time :datetime="page.updated_at" :title="updatedAtAbsolute" class="whitespace-nowrap shrink-0 hidden sm:inline"
        >updated {{ updatedAt }}</time
      >
    </div>
    <!-- Admin actions (Share/Edit/Rename/Export/Delete, desktop
         buttons + mobile hamburger + their modals) split into
         WikiPageMetaActions (#1097) — only ever mounted for admins. -->
    <WikiPageMetaActions v-if="isAdmin" :page="page" @cleared="emit('cleared')" />
  </div>
</template>
